import {
  directorDocColumns,
  directorPaidColumns,
  directorWIPColumns,
  hobDocColumns,
  hobPaidColumns,
  hobWIPColumns,
  hobWIPHeaderColumns,
  osmDocColumns,
  osmPaidColumns,
  osmWIPColumns,
  osmWIPHeaderColumns,
  predictionColumns,
  teamDocColumns,
  teamPaidColumns,
  teamWIPColumns,
  teamWIPHeaderColumns,
  wipHeaderColumns,
} from "@/constants/pipeline";
import { sortPipelineData } from "@/helpers/sorting";
import { DashboardPaidModel, DashboardPipelineModel, DashboardWIPWithTotals } from "@/models/pipeline";
import { useMemo, useState } from "react";
import { ComparisonBadge } from "./ComparisonBadge";
import { DashboardRow } from "./DashboardRow";
import { StaticPipelineCard } from "./StaticPipelineCard";

type EnrichedPaidModel = DashboardPaidModel & {
  gap: number | null;
  pctFromOriginalGap: number | null;
  pctPaidOverall: number | null;
};

export type DashboardViewVariant = "director" | "hob" | "osm" | "team";

type DashboardViewProps = {
  data: DashboardPipelineModel;
  variant?: DashboardViewVariant;
  onNameClick?: (teamName: string) => void;
};

export const DashboardView = ({ data, variant = "director", onNameClick }: DashboardViewProps) => {
  const [sortState, setSortState] = useState<{
    [key: string]: { sortBy: string; sortOrder: "asc" | "desc" } | null;
  }>({
    docs: null,
    payouts: null,
    wip: null,
  });

  const isClickable = variant === "hob" || variant === "osm" || variant === "team";

  const docColumns = useMemo(() => {
    if (variant === "director") return directorDocColumns;
    if (variant === "hob") return hobDocColumns;
    if (variant === "osm") return osmDocColumns;
    return teamDocColumns;
  }, [variant]);

  const paidColumns = useMemo(() => {
    if (variant === "director") return directorPaidColumns;
    if (variant === "hob") return hobPaidColumns;
    if (variant === "osm") return osmPaidColumns;
    return teamPaidColumns;
  }, [variant]);

  const wipColumns = useMemo(() => {
    if (variant === "director") return directorWIPColumns;
    if (variant === "hob") return hobWIPColumns;
    if (variant === "osm") return osmWIPColumns;
    return teamWIPColumns;
  }, [variant]);

  const wipHeaderColumnsMemo = useMemo(() => {
    if (variant === "director") return wipHeaderColumns;
    if (variant === "hob") return hobWIPHeaderColumns;
    if (variant === "osm") return osmWIPHeaderColumns;
    return teamWIPHeaderColumns;
  }, [variant]);

  const handleSort = (section: string, sortKey: string) => {
    setSortState((prev) => {
      const currentSort = prev[section];

      if (!currentSort || currentSort.sortBy !== sortKey) {
        return {
          ...prev,
          [section]: { sortBy: sortKey, sortOrder: "asc" },
        };
      } else if (currentSort.sortOrder === "asc") {
        return {
          ...prev,
          [section]: { sortBy: sortKey, sortOrder: "desc" },
        };
      } else {
        return {
          ...prev,
          [section]: null,
        };
      }
    });
  };

  const sortedDocs = useMemo(() => {
    let sorted = [...data.docs];
    const docsSort = sortState["docs"];
    if (docsSort) {
      sorted = sortPipelineData(sorted, docsSort.sortBy, docsSort.sortOrder);
    }
    return sorted;
  }, [data.docs, sortState]);

  const wipWithTotals = useMemo((): DashboardWIPWithTotals[] => {
    return data.wiPs.map((w) => ({
      ...w,
      beforeOpeningTotal: w.docsOutBeforeOpening + w.readyToGoBeforeOpening + w.payoutBeforeOpening,
      liveTotal: w.docsOutLive + w.readyToGoLive + w.payoutLive,
    }));
  }, [data.wiPs]);

  const wipGapById = useMemo(() => {
    const map = new Map<number, number>();
    wipWithTotals.forEach((w) => map.set(w.id, w.beforeOpeningTotal));
    return map;
  }, [wipWithTotals]);

  const wipGapByName = useMemo(() => {
    const map = new Map<string, number>();
    wipWithTotals.forEach((w) => map.set(w.name, w.beforeOpeningTotal));
    return map;
  }, [wipWithTotals]);

  const enrichedPayouts = useMemo((): EnrichedPaidModel[] => {
    return data.payouts.map((p) => {
      const gap = wipGapById.has(p.id) ? wipGapById.get(p.id)! : (wipGapByName.get(p.name) ?? null);
      const pctFromOriginalGap = gap != null && gap !== 0 ? (p.paidFromStartOfTheDay / gap) * 100 : null;
      const pctPaidOverall = gap != null && gap !== 0 ? (p.actual / gap) * 100 : null;
      return { ...p, gap, pctFromOriginalGap, pctPaidOverall };
    });
  }, [data.payouts, wipGapById, wipGapByName]);

  const sortedPaid = useMemo(() => {
    let sorted = [...enrichedPayouts];
    const paidSort = sortState["payouts"];
    if (paidSort) {
      sorted = sortPipelineData(sorted, paidSort.sortBy, paidSort.sortOrder);
    }
    return sorted;
  }, [enrichedPayouts, sortState]);

  const sortedWip = useMemo(() => {
    let sorted = [...wipWithTotals];
    const wipSort = sortState["wip"];
    if (wipSort) {
      sorted = sortPipelineData(sorted, wipSort.sortBy, wipSort.sortOrder);
    }
    return sorted;
  }, [wipWithTotals, sortState]);

  const showPredictions = variant === "osm" || variant === "team";

  return (
    <>
      <div className="flex flex-col gap-3 md:flex-row">
        <div className="w-full md:basis-1/2">
          <StaticPipelineCard
            title="Docs"
            columns={docColumns}
            sortBy={sortState["docs"]?.sortBy}
            sortOrder={sortState["docs"]?.sortOrder}
            onSort={(sortKey) => handleSort("docs", sortKey)}
          >
            {sortedDocs.map((item, index) => (
              <DashboardRow
                key={`doc-${index}`}
                name={item.name}
                columns={docColumns}
                sortBy={sortState["docs"]?.sortBy}
                highlightColor="#FBFAFF"
                isClickable={isClickable}
                onNameClick={onNameClick}
              >
                <span>{item.predicted}</span>
                <ComparisonBadge actual={item.predictedActual} predicted={item.predicted} />
                <ComparisonBadge actual={item.actual} predicted={item.predicted} />
                <span>{item.totalReDoc}</span>
              </DashboardRow>
            ))}
          </StaticPipelineCard>
        </div>
        <div className="w-full md:basis-1/2">
          <StaticPipelineCard
            title="Paid"
            columns={paidColumns}
            sortBy={sortState["payouts"]?.sortBy}
            sortOrder={sortState["payouts"]?.sortOrder}
            onSort={(sortKey) => handleSort("payouts", sortKey)}
            useFixedLayout={false}
          >
            {sortedPaid.map((item, index) => (
              <DashboardRow
                key={`paid-${index}`}
                name={item.name}
                columns={paidColumns}
                sortBy={sortState["payouts"]?.sortBy}
                highlightColor="#FBFAFF"
                isClickable={isClickable}
                onNameClick={onNameClick}
              >
                <span>{item.gap ?? "-"}</span>
                <span>{item.predicted}</span>
                <span>{item.actual}</span>
                <span>{item.pctFromOriginalGap != null ? `${Math.round(item.pctFromOriginalGap)}%` : "-"}</span>
                <span>{item.pctPaidOverall != null ? `${Math.round(item.pctPaidOverall)}%` : "-"}</span>
              </DashboardRow>
            ))}
          </StaticPipelineCard>
        </div>
      </div>
      <StaticPipelineCard
        title="WIP"
        columns={wipColumns}
        headerColumns={wipHeaderColumnsMemo}
        sortBy={sortState["wip"]?.sortBy}
        sortOrder={sortState["wip"]?.sortOrder}
        onSort={(sortKey) => handleSort("wip", sortKey)}
      >
        {sortedWip.map((item, index) => (
          <DashboardRow
            key={`wip-${index}`}
            name={item.name}
            columns={wipColumns}
            sortBy={sortState["wip"]?.sortBy}
            highlightColor="#FBFAFF"
            isClickable={isClickable}
            onNameClick={onNameClick}
          >
            <span>{item.docsOutBeforeOpening}</span>
            <span>{item.readyToGoBeforeOpening}</span>
            <span>{item.payoutBeforeOpening}</span>
            <span>{item.beforeOpeningTotal}</span>
            <span>{item.docsOutLive}</span>
            <span>{item.readyToGoLive}</span>
            <span>{item.payoutLive}</span>
            <span>{item.liveTotal}</span>
          </DashboardRow>
        ))}
      </StaticPipelineCard>
      {showPredictions && (
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="w-full md:basis-1/2">
            <StaticPipelineCard title="Predicted to Doc" columns={predictionColumns}>
              {data.predictionsDoc?.map((item, index) => (
                <DashboardRow
                  key={`pred-doc-${item.applicationId}-${index}`}
                  name={item.customerName}
                  columns={predictionColumns}
                  highlightColor="#FBFAFF"
                >
                  <span>{item.applicationStatusName}</span>
                  <span>{item.applicationId}</span>
                  <span>{item.agentName}</span>
                  <span>{item.departmentName}</span>
                  <span>{item.daysSincePrediction}</span>
                </DashboardRow>
              ))}
            </StaticPipelineCard>
          </div>
          <div className="w-full md:basis-1/2">
            <StaticPipelineCard title="Predicted to Pay" columns={predictionColumns}>
              {data.predictionsPaid?.map((item, index) => (
                <DashboardRow
                  key={`pred-paid-${item.applicationId}-${index}`}
                  name={item.customerName}
                  columns={predictionColumns}
                  highlightColor="#FBFAFF"
                >
                  <span>{item.applicationStatusName}</span>
                  <span>{item.applicationId}</span>
                  <span>{item.agentName}</span>
                  <span>{item.departmentName}</span>
                  <span>{item.daysSincePrediction}</span>
                </DashboardRow>
              ))}
            </StaticPipelineCard>
          </div>
        </div>
      )}
    </>
  );
};
